// frontend/src/components/finance/CapitalModule.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, toDate, today } from '../common/CommonComponents';

function CapitalModule({ showAlert, user }) {
  const blankC = { capitalId: "", source: "", contributorName: "", amountReceived: "", notes: "", capitalKind: "Loan", equityPercentage: "", isRepayable: true, interestRate: "", startDate: today(), dueDate: "" };
  const blankR = { repaymentId: "", capitalId: "", amount: "", date: today(), notes: "" };
  const [capForm, setCapForm] = useState(blankC);
  const [repForm, setRepForm] = useState(blankR);
  const [capMode, setCapMode] = useState("create");
  const [capitals, setCapitals] = useState([]);
  const [netWorth, setNetWorth] = useState(0);
  const [repayments, setRepayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("capital");
  const [selectedCapital, setSelectedCapital] = useState(null);
  const [showCapForm, setShowCapForm] = useState(false);
  const [showRepayForm, setShowRepayForm] = useState(false);
  const capOperationId=useRef(null),repOperationId=useRef(null),capSubmitting=useRef(false),repSubmitting=useRef(false);
  const makeOperationId=()=>window.crypto?.randomUUID?.()||`op-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const load = async () => {
    setLoading(true);
    try {
      const [caps, reps, dash] = await Promise.all([
        api.getCapital(),
        api.getRepayments(),
        api.getDashboard()
      ]);
      setCapitals(caps);
      setNetWorth(Number(dash.netWorth || 0));
      setRepayments(reps);
    } catch(e) {
      showAlert(e.message, "error");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setC = (k, v) => setCapForm(f => ({ ...f, [k]: v }));
  const setR = (k, v) => setRepForm(f => ({ ...f, [k]: v }));

  const submitCap = async () => {
    if (!capForm.source || !capForm.amountReceived) {
      showAlert("Fill all fields", "error");
      return;
    }
    if(capSubmitting.current)return;
    capSubmitting.current=true;
    try {
      if (capMode === "create") {
        if(!capOperationId.current)capOperationId.current=makeOperationId();
        await api.createCapital({
          source: capForm.source,
          contributorName: capForm.contributorName || capForm.source,
          amountReceived: Number(capForm.amountReceived),
          notes: capForm.notes,
          isRepayable: capForm.capitalKind === "Loan",
          capitalKind: capForm.capitalKind,
          equityPercentage: capForm.capitalKind === "InvestorEquity" ? Number(capForm.equityPercentage) : 0,
          interestRate: Number(capForm.interestRate)||0,
          startDate: capForm.startDate,
          dueDate: capForm.dueDate || null
        },capOperationId.current);
        showAlert("Capital recorded!");
      } else {
        await api.updateCapital(capForm.capitalId, {
          source: capForm.source,
          contributorName: capForm.contributorName || capForm.source,
          amountReceived: Number(capForm.amountReceived),
          interestRate: Number(capForm.interestRate)||0,
          startDate: capForm.startDate,
          dueDate: capForm.dueDate || null,
          notes: capForm.notes
        });
        showAlert("Capital updated!");
      }
      capOperationId.current=null;
      setCapForm(blankC);
      setCapMode("create");
      setShowCapForm(false);
      load();
    } catch(e) {
      showAlert(e.message, "error");
    } finally { capSubmitting.current=false; }
  };

  const submitRep = async () => {
    if (!repForm.capitalId || !repForm.amount) {
      showAlert("Fill all fields", "error");
      return;
    }
    if(repSubmitting.current)return;
    repSubmitting.current=true;
    try {
      if(!repOperationId.current)repOperationId.current=makeOperationId();
      await api.createRepayment({
        capitalId: repForm.capitalId,
        amount: Number(repForm.amount),
        date: repForm.date,
        notes: repForm.notes
      },repOperationId.current);
      showAlert("Repayment recorded!");
      repOperationId.current=null;
      setRepForm(blankR);
      setSelectedCapital(null);
      setShowRepayForm(false);
      load();
    } catch(e) {
      showAlert(e.message, "error");
    } finally { repSubmitting.current=false; }
  };

  const cancelEdit = () => {
    capOperationId.current=null;
    setCapForm(blankC);
    setCapMode("create");
    setShowCapForm(false);
  };

  const cancelRepay = () => {
    repOperationId.current=null;
    setRepForm(blankR);
    setSelectedCapital(null);
    setShowRepayForm(false);
  };

  const openEditCapital = (c) => {
    setCapForm({
      capitalId: c.capitalId,
      source: c.source,
      contributorName: c.contributorName || c.source,
      amountReceived: c.amountReceived,
      notes: c.notes || "",
      capitalKind: c.capitalKind || (c.isRepayable ? "Loan" : "OwnerEquity"),
      equityPercentage: c.equityPercentage || "",
      isRepayable: c.isRepayable,
      interestRate: c.interestRate || "",
      startDate: c.startDate ? String(c.startDate).slice(0,10) : today(),
      dueDate: c.dueDate ? String(c.dueDate).slice(0,10) : ""
    });
    setCapMode("update");
    setShowCapForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const repayableCapitals = capitals.filter(c => c.isRepayable === true);
  const nonRepayableCapitals = capitals.filter(c => c.isRepayable === false);
  
  const totalBorrowed = repayableCapitals.reduce((sum, c) => sum + (c.amountReceived || 0), 0);
  const totalRepaid = repayments.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalOutstanding = repayableCapitals.reduce((sum, c) => sum + (c.balance || 0), 0);
  const totalInjections = nonRepayableCapitals.reduce((sum, c) => sum + (c.amountReceived || 0), 0);
  const completionRate = totalBorrowed > 0 ? ((totalRepaid / totalBorrowed) * 100).toFixed(1) : "0.0";

  const getStatusStyle = (status) => {
    switch(status) {
      case "Active": return { bg: "#10B98120", color: "#10B981", icon: "✅" };
      case "Ongoing": return { bg: "#3B82F620", color: "#3B82F6", icon: "🔄" };
      case "Completed": return { bg: "#10B98120", color: "#10B981", icon: "🏁" };
      default: return { bg: "#94A3B820", color: "#94A3B8", icon: "📋" };
    }
  };

  const getTypeStyle = (c) => {
    if (c.capitalKind === "InvestorEquity") return { bg:"#8B5CF620", color:"#8B5CF6", icon:"🤝", label:"Investor Equity" };
    return c.isRepayable
      ? { bg: "#EF444420", color: "#EF4444", icon: "🏦", label: "Repayable Loan" }
      : { bg: "#10B98120", color: "#10B981", icon: "💵", label: "Owner Equity" };
  };

  if (loading) {
    return <div className="spinner">Loading capital data...</div>;
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>💰 Capital Management</h2>
          <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>
            Track owner equity, investor equity, loans, ownership dilution, and repayment schedules
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setShowRepayForm(true)}>
            💸 Record Repayment
          </button>
          <button className="btn btn-primary" onClick={() => { setCapMode("create"); setCapForm(blankC); setShowCapForm(true); }}>
            + Add Capital
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: "4px solid #EF4444" }}>
          <div className="lbl">Total Borrowed</div>
          <div className="val danger">{fmt(totalBorrowed)}</div>
          <div className="stat-sub">From {repayableCapitals.length} loan(s)</div>
        </div>
        <div className="stat-card" style={{ borderLeft: "4px solid #10B981" }}>
          <div className="lbl">Total Repaid</div>
          <div className="val success">{fmt(totalRepaid)}</div>
          <div className="stat-sub">{completionRate}% repaid</div>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: "4px solid #F59E0B" }}>
          <div className="lbl">Outstanding Debt</div>
          <div className="val warning">{fmt(totalOutstanding)}</div>
          <div className="stat-sub">Remaining balance</div>
        </div>
        <div className="stat-card" style={{ borderLeft: "4px solid #3B82F6" }}>
          <div className="lbl">Owner Injections</div>
          <div className="val info">{fmt(totalInjections)}</div>
          <div className="stat-sub">Non-repayable capital</div>
        </div>
      </div>

      {totalBorrowed > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)" }}>Overall Repayment Progress</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#10B981" }}>{completionRate}%</span>
          </div>
          <div style={{ height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${completionRate}%`, height: "100%", background: "#10B981", borderRadius: 4, transition: "width 0.3s" }} />
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-3)" }}>
            ₦{totalRepaid.toLocaleString()} repaid of ₦{totalBorrowed.toLocaleString()}
          </div>
        </div>
      )}

      {showCapForm && (
        <div className="card" style={{ marginBottom: 20, border: capMode === "update" ? "2px solid #F59E0B" : "2px solid var(--accent)" }}>
          <div className="section-header">
            <div>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                {capMode === "create" ? "➕ Add New Capital" : "✏️ Edit Capital Record"}
              </h2>
              {capMode === "update" && <span className="mode-tag">UPDATE MODE</span>}
            </div>
            <button className="btn btn-secondary" onClick={cancelEdit}>✕ Cancel</button>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Source *</label>
              <input value={capForm.source} onChange={e => setC("source", e.target.value)} placeholder="Bank, investor, personal..." />
            </div>
            <div className="form-group">
              <label>Amount Received (₦) *</label>
              <input type="number" value={capForm.amountReceived} onChange={e => setC("amountReceived", e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Contributor / Investor</label>
            <input value={capForm.contributorName || ""} onChange={e => setC("contributorName", e.target.value)} placeholder="Person or entity contributing this capital" />
          </div>
          <div className="form-group">
            <label>Capital Type</label>
            <select value={capForm.capitalKind} disabled={capMode==="update"} onChange={e => { const kind=e.target.value; setC("capitalKind",kind); setC("isRepayable",kind==="Loan"); }}>
              <option value="Loan">🏦 Repayable Loan</option>
              <option value="OwnerEquity">💵 Owner Equity — non-repayable</option>
              <option value="InvestorEquity">🤝 Investor Equity — non-repayable ownership</option>
            </select>
            {capMode==="update" && <div style={{fontSize:10,color:"var(--text-3)",marginTop:4}}>Capital type is fixed after recording so financing history stays auditable.</div>}
          </div>
          {capForm.capitalKind==="InvestorEquity" && <div className="form-group">
            <label>Equity Granted (%) *</label>
            <input type="number" min="0.0001" max="100" step="0.01" disabled={capMode==="update"} value={capForm.equityPercentage} onChange={e=>setC("equityPercentage",e.target.value)} />
            <div style={{fontSize:10,color:"var(--text-3)",marginTop:4}}>This is the investor's post-investment ownership. New investor equity dilutes existing investor percentages proportionally; total ownership remains 100% including the owner's residual share.</div>
          </div>}
          {capForm.capitalKind==="Loan" && <div className="form-row">
            <div className="form-group"><label>Annual Interest Rate (%)</label>
              <input type="number" min="0" step="0.01" value={capForm.interestRate} onChange={e => setC("interestRate", e.target.value)} /></div>
            <div className="form-group"><label>Loan Start Date *</label>
              <input type="date" value={capForm.startDate} onChange={e => setC("startDate", e.target.value)} /></div>
            <div className="form-group"><label>Agreed Repayment Date *</label>
              <input type="date" value={capForm.dueDate} onChange={e => setC("dueDate", e.target.value)} /></div>
          </div>}
          <div className="form-group">
            <label>Notes</label>
            <input value={capForm.notes} onChange={e => setC("notes", e.target.value)} placeholder="Terms, interest rate, repayment schedule..." />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" onClick={submitCap}>
              {capMode === "create" ? "➕ Add Capital" : "💾 Update Capital"}
            </button>
            <button className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      )}

      {showRepayForm && (
        <div className="card" style={{ marginBottom: 20, border: "2px solid #10B981" }}>
          <div className="section-header">
            <div>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>💸 Record Repayment</h2>
            </div>
            <button className="btn btn-secondary" onClick={cancelRepay}>✕ Cancel</button>
          </div>
          <div className="form-group">
            <label>Capital Record *</label>
            <select value={repForm.capitalId} onChange={e => {
              setR("capitalId", e.target.value);
              const cap = capitals.find(c => c.capitalId === e.target.value);
              setSelectedCapital(cap);
            }}>
              <option value="">— Select Capital Record —</option>
              {capitals.filter(c => c.isRepayable === true && c.status !== "Completed").map(c => (
                <option key={c.capitalId} value={c.capitalId}>
                  {c.capitalId} | {c.source} | Balance: {fmt(c.balance)} | Status: {c.status}
                </option>
              ))}
            </select>
          </div>
          {selectedCapital && (
            <div className="alert alert-info" style={{ marginBottom: 12, fontSize: 12 }}>
              <div><strong>Capital Record:</strong> {selectedCapital.source}</div>
              <div><strong>Principal:</strong> {fmt(selectedCapital.amountReceived)}</div>
              <div><strong>Fixed Contract Total:</strong> {fmt(selectedCapital.contractualTotal || selectedCapital.amountReceived)}</div>
              <div><strong>Agreed Repayment Date:</strong> {selectedCapital.dueDate ? toDate(selectedCapital.dueDate) : "—"}</div>
              <div><strong>Repaid So Far:</strong> {fmt(selectedCapital.totalRepaid)}</div>
              <div><strong>Outstanding Balance:</strong> {fmt(selectedCapital.balance)}</div>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Amount (₦) *</label>
              <input type="number" value={repForm.amount} onChange={e => setR("amount", e.target.value)} max={selectedCapital?.balance} />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={repForm.date} onChange={e => setR("date", e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <input value={repForm.notes} onChange={e => setR("notes", e.target.value)} placeholder="Payment reference, cheque number, etc." />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-success" onClick={submitRep}>💸 Record Repayment</button>
            <button className="btn btn-secondary" onClick={cancelRepay}>Cancel</button>
          </div>
        </div>
      )}

      <div className="tab-switch">
        <button className={activeTab === "capital" ? "active" : ""} onClick={() => setActiveTab("capital")}>
          📊 Capital Records ({capitals.length})
        </button>
        <button className={activeTab === "repayments" ? "active" : ""} onClick={() => setActiveTab("repayments")}>
          📋 Repayment Records ({repayments.length})
        </button>
      </div>

      {activeTab === "capital" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="table-wrap">
            <table className="w-full">
              <thead>
                <tr>
                  <th>ID</th><th>Contributor</th><th>Source</th><th>Type</th><th>Principal</th><th>Equity</th><th>Potential Value</th><th>Interest</th><th>Agreed Due</th><th>Repaid</th><th>Balance</th><th>Progress</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {capitals.length === 0 ? (
                  <tr><td colSpan="10" style={{ textAlign: "center", color: "var(--text-3)", padding: 32 }}>No capital records yet</td></tr>
                ) : (
                  capitals.map(c => {
                    const typeStyle = getTypeStyle(c);
                    const statusStyle = getStatusStyle(c.status);
                    const progressPercent = c.amountReceived > 0 ? ((c.totalRepaid / c.amountReceived) * 100).toFixed(0) : 0;
                    return (
                      <tr key={c.capitalId}>
                        <td style={{ fontFamily: "monospace", fontSize: 11 }}>{c.capitalId}</td>
                        <td style={{ fontWeight: 600 }}>{c.contributorName || c.source}</td>
                        <td style={{ fontWeight: 600 }}>{c.source}</td>
                        <td><span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: typeStyle.bg, color: typeStyle.color }}>{typeStyle.icon} {typeStyle.label}</span></td>
                        <td style={{ fontWeight: 700 }}>{fmt(c.amountReceived)}</td>
                        <td>{c.capitalKind==="InvestorEquity" ? `${Number(c.equityPercentage||0).toFixed(2)}%` : "—"}</td>
                        <td style={{fontWeight:700,color:"#8B5CF6"}}>{c.capitalKind==="InvestorEquity" ? fmt(netWorth * Number(c.equityPercentage||0) / 100) : "—"}</td>
                        <td>{c.isRepayable ? `${c.interestRate || 0}% / ${fmt(c.contractualInterest || 0)}` : "—"}</td>
                        <td>{c.dueDate ? toDate(c.dueDate) : "—"}</td>
                        <td style={{ fontWeight: 600, color: "#10B981" }}>{fmt(c.totalRepaid)}</td>
                        <td style={{ fontWeight: 700, color: c.balance > 0 ? "#EF4444" : "#10B981" }}>{fmt(c.balance)}</td>
                        <td><div style={{ width: 80 }}><div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, marginBottom: 2 }}><span>{progressPercent}%</span></div><div style={{ height: 4, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}><div style={{ width: `${progressPercent}%`, height: "100%", background: "#10B981", borderRadius: 2 }} /></div></div></td>
                        <td><span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.icon} {c.status}</span></td>
                        <td><button className="btn-sm btn-edit" onClick={() => openEditCapital(c)}>Edit</button></td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "repayments" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="table-wrap">
            <table className="w-full">
              <thead>
                <tr><th>ID</th><th>Capital Record</th><th>Amount</th><th>Date</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {repayments.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--text-3)", padding: 32 }}>No repayment records yet</td></tr>
                ) : (
                  repayments.map(r => {
                    const capital = capitals.find(c => c.capitalId === r.capitalId);
                    return (
                      <tr key={r.repaymentId}>
                        <td style={{ fontFamily: "monospace", fontSize: 11 }}>{r.repaymentId}</td>
                        <td style={{ fontWeight: 600 }}>{capital?.source || r.capitalId}</td>
                        <td style={{ fontWeight: 700, color: "#10B981" }}>{fmt(r.amount)}</td>
                        <td style={{ fontSize: 11 }}>{toDate(r.date)}</td>
                        <td style={{ fontSize: 11, color: "var(--text-3)" }}>{r.notes || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {nonRepayableCapitals.length > 0 && (
        <div className="alert alert-info" style={{ marginTop: 16 }}>
          💡 <strong>Equity guidance:</strong> Owner and investor equity are non-repayable financing, not debts. An investor's Potential Value is an indicative share of current ERP Net Worth (Net Worth × current equity %) — it is not an amount automatically payable on exit. New investor equity dilutes existing ownership; the business never has more than 100% ownership.
        </div>
      )}
    </div>
  );
}

export default CapitalModule;